/**
 * Notification channels — destinations for alerts.
 *
 * Lists every configured channel (in_app singleton + N external).
 * Test button on each row fires a "[Tray] test" message through the
 * dispatcher and shows the result inline. Singleton row can't be
 * deleted but can be disabled (silences the bell).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Megaphone,
  Pencil,
  Plus,
  TestTube2,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { ChannelDialog, type DialogMode } from "@/components/channels/ChannelDialog";
import { TagList } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { channelsApi, type ChannelType } from "@/lib/api-channels";
import { usePermissions } from "@/lib/permissions";


const TYPE_LABEL: Record<ChannelType, string> = {
  in_app: "In-app",
  email: "Email",
  telegram: "Telegram",
};


export function ChannelsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("channels", "write");
  const canDelete = can("channels", "delete");

  const [dialog, setDialog] = React.useState<{ open: boolean; mode: DialogMode }>({
    open: false,
    mode: { kind: "create" },
  });

  const listQ = useQuery({
    queryKey: ["channels"],
    queryFn: () => channelsApi.list(),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      channelsApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels"] }),
  });

  const remove = useMutation({
    mutationFn: channelsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels"] }),
  });

  const [testTarget, setTestTarget] = React.useState<string | null>(null);
  const test = useMutation({
    mutationFn: channelsApi.test,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels"] }),
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("channels.title", "Notification channels")}
        description={t(
          "channels.subtitle",
          "Destinations for alerts. Configure once, reference from many alert rules.",
        )}
        actions={
          canWrite && (
            <Button
              size="sm"
              onClick={() => setDialog({ open: true, mode: { kind: "create" } })}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("channels.new", "New channel")}
            </Button>
          )
        }
      />
      <PageContent>
        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Megaphone}
            title={t("channels.empty", "No channels yet")}
            description={t(
              "channels.emptyDescription",
              "Add email recipients, a Telegram bot — anywhere alerts should be delivered besides the bell.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-12">On</Th>
                  <Th>Name</Th>
                  <Th className="w-24">Type</Th>
                  <Th>Tags</Th>
                  <Th>Last used</Th>
                  <Th>Last error</Th>
                  {(canWrite || canDelete) && (
                    <Th className="w-40 text-right">Actions</Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((c) => (
                  <React.Fragment key={c.id}>
                    <Tr>
                      <Td onClick={(e) => e.stopPropagation()}>
                        {canWrite ? (
                          <Switch
                            checked={c.enabled}
                            onCheckedChange={(v) =>
                              toggle.mutate({ id: c.id, enabled: v })
                            }
                          />
                        ) : (
                          <Badge tone={c.enabled ? "active" : "neutral"}>
                            {c.enabled ? "on" : "off"}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div className="flex items-center gap-2 font-medium">
                          {c.name}
                          {c.is_singleton && (
                            <Badge tone="neutral" className="text-[10px]">
                              singleton
                            </Badge>
                          )}
                          <DescriptionTooltip text={c.description} />
                        </div>
                      </Td>
                      <Td className="font-mono text-xs">{TYPE_LABEL[c.type] ?? c.type}</Td>
                      <Td>
                        <TagList tags={c.tags} max={3} />
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {c.last_used_at ? new Date(c.last_used_at).toLocaleString() : "—"}
                      </Td>
                      <Td className="max-w-[18rem] truncate text-xs text-danger">
                        {c.last_error ?? ""}
                      </Td>
                      {(canWrite || canDelete) && (
                        <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Test"
                                title="Send a test message"
                                onClick={() => {
                                  setTestTarget(c.id);
                                  test.mutate(c.id);
                                }}
                              >
                                <TestTube2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canWrite && !c.is_singleton && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Edit"
                                onClick={() =>
                                  setDialog({
                                    open: true,
                                    mode: { kind: "edit", channel: c },
                                  })
                                }
                              >
                                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && !c.is_singleton && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Delete"
                                onClick={() => {
                                  if (confirm(`Delete channel "${c.name}"?`)) {
                                    remove.mutate(c.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </Td>
                      )}
                    </Tr>
                    {testTarget === c.id && test.data && (
                      <Tr>
                        <Td colSpan={canWrite || canDelete ? 7 : 6} className="!py-0">
                          <Alert
                            tone={test.data.ok ? "info" : "danger"}
                            title={test.data.ok ? "Test sent" : "Test failed"}
                            className="my-2"
                          >
                            <div className="flex items-start gap-2 font-mono text-xs">
                              {test.data.ok ? (
                                <CheckCircle2
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success"
                                  strokeWidth={1.5}
                                />
                              ) : (
                                <AlertCircle
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger"
                                  strokeWidth={1.5}
                                />
                              )}
                              <span>{test.data.message}</span>
                            </div>
                          </Alert>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </div>
        )}

        {/* Help hint about the in_app singleton, surfaces in case the user wonders why they can't delete it. */}
        <p className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Bell className="h-3 w-3" strokeWidth={1.5} />
          The <span className="font-mono">in_app</span> channel is the bell + activity feed. Always present; can be disabled.
        </p>
      </PageContent>

      <ChannelDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
    </PageContainer>
  );
}
